import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Box, Button, Container, Grid, Stack, Typography, Skeleton } from '@mui/material';
import { FitnessCenter as FitnessCenterIcon, CalendarToday as CalendarIcon, Layers as LayersIcon, LocalFireDepartment as StreakIcon } from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import AppBarContext from 'context/AppBarContext';
import StatCard from 'components/gamification/StatCard';
import PlayPerformanceExercise from 'components/play/PlayPerformanceExercise';
import PlayResultLoggerModal from 'components/play/performance/PlayResultLoggerModal';
import { premiumButtonSx } from 'common/theme/button/premiumButtonSx';
import { countSeries, distinctSeries, trendOf } from 'components/play/weeklyTrend';
import { MY_PLAY_RESULTS, MY_PLAY_WORKOUT_SUMMARY } from 'api/graphql/play';

const fmtDate = (iso) => {
  try {
    return format(parseISO(iso), 'd MMM yyyy', { locale: es });
  } catch {
    return '';
  }
};

/**
 * OTRIX Play — "Mi rendimiento" del cliente Play, con el mismo lenguaje visual que el del
 * entrenador (OTRIXVERSE): tarjetas de resumen + una fila por ejercicio con "mi posición" que
 * abre el detalle (Tu posición, Top 3, gráfica de progreso e historial). Alimentado por los
 * resultados auto-reportados del usuario.
 */
function PlayPerformanceHistory() {
  const { t } = useTranslation(['play', 'routes']);
  const pageTitle = t('performance.myPerformance', { ns: 'routes', defaultValue: 'Mi rendimiento' });
  const vsLastWeek = t('perf.vsLastWeek', { ns: 'play', defaultValue: 'vs semana anterior' });
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);
  const [loggerOpen, setLoggerOpen] = useState(false);

  useEffect(() => {
    setAppBarButton(null);
    setAppBarTitle(pageTitle);
  }, [pageTitle, setAppBarButton, setAppBarTitle]);

  const { data, loading } = useQuery(MY_PLAY_RESULTS, { variables: {}, fetchPolicy: 'cache-and-network' });
  const results = useMemo(() => data?.myPlayResults || [], [data]);
  // Racha: vivía en la card "Tu semana" del home (eliminada por redundante); ahora es
  // una métrica de primera clase de Rendimientos.
  const { data: sumData } = useQuery(MY_PLAY_WORKOUT_SUMMARY, { fetchPolicy: 'cache-and-network' });
  const streakDays = sumData?.myPlayWorkoutSummary?.current_streak_days || 0;

  const groups = useMemo(() => {
    const map = new Map();
    results.forEach((r) => {
      const ex = r.exercise;
      if (!ex) return;
      if (!map.has(ex.id)) map.set(ex.id, { exercise: ex, items: [] });
      map.get(ex.id).items.push(r);
    });
    return Array.from(map.values());
  }, [results]);

  const lastActivity = useMemo(() => {
    const dates = results.map((r) => r.created_at).filter(Boolean);
    if (!dates.length) return null;
    return fmtDate(dates.sort((a, b) => new Date(b) - new Date(a))[0]);
  }, [results]);

  // Series de las últimas 8 semanas para los sparklines + la variación semana vs. semana anterior.
  const trends = useMemo(() => {
    const getDate = (r) => r.created_at;
    const resultsSeries = countSeries(results, getDate);
    const typesSeries = distinctSeries(results, getDate, (r) => r.exercise?.id);
    return { resultsSeries, typesSeries };
  }, [results]);

  const summaryCards = [
    {
      icon: FitnessCenterIcon,
      value: results.length,
      label: t('perf.totalResults', { ns: 'play', defaultValue: 'Resultados registrados' }),
      sparkline: trends.resultsSeries,
      ...(trendOf(trends.resultsSeries) || {})
    },
    {
      icon: LayersIcon,
      value: groups.length,
      label: t('perf.exerciseTypes', { ns: 'play', defaultValue: 'Tipos de ejercicio' }),
      sparkline: trends.typesSeries,
      ...(trendOf(trends.typesSeries) || {})
    },
    {
      icon: StreakIcon,
      value: t('perfSummary.days', { ns: 'play', count: streakDays, defaultValue: '{{count}} días' }),
      label: t('perfSummary.streak', { ns: 'play', defaultValue: 'Racha actual' })
    },
    {
      icon: CalendarIcon,
      value: lastActivity || '—',
      label: t('perf.lastActivity', { ns: 'play', defaultValue: 'Última actividad' }),
      // Sin badge de tendencia: un "%" sobre una fecha no significa nada.
      sparkline: trends.resultsSeries
    }
  ];

  return (
    <>
      <SEOPageTitle title={pageTitle} />
      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Registro manual de marcas (con foto): complementa las que derivan los entrenamientos. */}
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
          <Button onClick={() => setLoggerOpen(true)} sx={{ ...premiumButtonSx }}>
            {t('logger.title', { ns: 'play', defaultValue: 'Registrar marca' })}
          </Button>
        </Stack>

        <Grid container spacing={2} sx={{ mb: 4 }}>
          {summaryCards.map((card) => {
            const Icon = card.icon;
            return (
              <Grid item xs={12} sm={6} md={3} key={card.label}>
                {loading && results.length === 0 ? (
                  <Skeleton variant="rectangular" height={132} sx={{ borderRadius: 2 }} />
                ) : (
                  <StatCard
                    title={card.label}
                    value={card.value}
                    icon={<Icon sx={{ fontSize: 22 }} />}
                    trend={card.trend}
                    trendValue={card.trendValue}
                    subtitle={card.trend ? vsLastWeek : undefined}
                    sparkline={card.sparkline}
                  />
                )}
              </Grid>
            );
          })}
        </Grid>

        {loading && results.length === 0 && (
          <Stack spacing={2}>
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rectangular" height={80} sx={{ borderRadius: 2 }} />
            ))}
          </Stack>
        )}

        {!loading && groups.length === 0 && (
          <Box sx={{ p: { xs: 4, md: 6 }, textAlign: 'center', backgroundColor: 'surface.content', border: '1px solid', borderColor: 'hairline.main', borderRadius: 3 }}>
            <FitnessCenterIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
              {t('perf.emptyTitle', { ns: 'play', defaultValue: 'Aún no tienes rendimientos' })}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 400, mx: 'auto' }}>
              {t('perf.emptyBody', { ns: 'play', defaultValue: 'Registra un resultado con foto desde cualquier ejercicio y aquí verás tu progreso y la calificación de tu entrenador.' })}
            </Typography>
          </Box>
        )}

        {groups.length > 0 && (
          <Stack spacing={2}>
            {groups.map((group) => (
              <PlayPerformanceExercise key={group.exercise.id} group={group} />
            ))}
          </Stack>
        )}

        <PlayResultLoggerModal open={loggerOpen} onClose={() => setLoggerOpen(false)} />
      </Container>
    </>
  );
}

export default PlayPerformanceHistory;
